// Nozzle interactive calculator for product pages.
// Reads configuration from data-* attributes on the #nozzleCalc container.
//
// Slider values and `data-*` payloads are always **metric** (m, L/min,
// mm/h, m²). The render step converts to the user's chosen unit system
// (issue #1466) so the live read-out matches the static spec table.

import {
  convertArea,
  convertFlow,
  convertLength,
  convertPrecipRate,
  getUnits,
} from '../shared/units.ts'

interface ArcEntry {
  arc: number
  radius: number
  flow: number
  precip: number
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

function bracket(arcsData: ArcEntry[], arc: number): { lower: ArcEntry; upper: ArcEntry; t: number } {
  let lower = arcsData[0]
  let upper = arcsData[arcsData.length - 1]
  for (let i = 0; i < arcsData.length - 1; i++) {
    if (arc >= arcsData[i].arc && arc <= arcsData[i + 1].arc) {
      lower = arcsData[i]
      upper = arcsData[i + 1]
      break
    }
  }

  const arcRange = upper.arc - lower.arc
  return { lower, upper, t: arcRange > 0 ? (arc - lower.arc) / arcRange : 0 }
}

function interpolate(arcsData: ArcEntry[], arc: number, radius: number): number {
  const { lower, upper, t } = bracket(arcsData, arc)
  const baseFlow = lerp(lower.flow, upper.flow, t)
  const baseRadius = lerp(lower.radius, upper.radius, t)

  const radiusRatio = baseRadius > 0 ? radius / baseRadius : 1
  return baseFlow * (radiusRatio * radiusRatio)
}

// Precipitation is flow spread over the sector, so it goes as flow / arc:
// inversely with the arc for a rotor (same flow at any setting), flat for a
// matched-precipitation nozzle (flow grows with the arc). Interpolating the
// reciprocal is exact for both; a straight lerp overshoots a rotor between
// rows. Radius does not enter: flow above scales with r², as does the area.
function precipitationAt(arcsData: ArcEntry[], arc: number): number {
  const { lower, upper, t } = bracket(arcsData, arc)
  if (lower.precip > 0 && upper.precip > 0) {
    return 1 / lerp(1 / lower.precip, 1 / upper.precip, t)
  }
  return lerp(lower.precip, upper.precip, t)
}

function drawArc(maxRadius: number, arc: number, radius: number): string {
  const scale = 85 / maxRadius
  const r = radius * scale
  const startAngle = -arc / 2
  const endAngle = arc / 2
  const startRad = ((startAngle - 90) * Math.PI) / 180
  const endRad = ((endAngle - 90) * Math.PI) / 180
  const x1 = 100 + r * Math.cos(startRad)
  const y1 = 100 + r * Math.sin(startRad)
  const x2 = 100 + r * Math.cos(endRad)
  const y2 = 100 + r * Math.sin(endRad)
  const largeArc = arc > 180 ? 1 : 0

  if (arc >= 360) {
    return `M 100 ${100 - r} A ${r} ${r} 0 1 1 100 ${100 + r} A ${r} ${r} 0 1 1 100 ${100 - r} Z`
  }
  return `M 100 100 L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`
}

export function initNozzleCalculator(): void {
  const container = document.getElementById('nozzleCalc')
  if (!container) return

  const arcsData: ArcEntry[] = JSON.parse(container.dataset.arcs || '[]')
  if (!arcsData.length) return

  const nozzleColor = container.dataset.nozzleColor || '#2d6a4f'
  const maxRadius = parseFloat(container.dataset.maxRadius || '0')

  const arcSlider = document.getElementById('calcArc') as HTMLInputElement | null
  const radiusSlider = document.getElementById('calcRadius') as HTMLInputElement | null
  const arcValue = document.getElementById('calcArcValue')
  const radiusValue = document.getElementById('calcRadiusValue')
  const flowEl = document.getElementById('calcFlow')
  const precipSqEl = document.getElementById('calcPrecipSq')
  const precipTriEl = document.getElementById('calcPrecipTri')
  const areaEl = document.getElementById('calcArea')
  const arcPath = document.getElementById('calcArcPath')

  if (!arcSlider || !radiusSlider || !arcValue || !radiusValue || !flowEl || !precipSqEl || !precipTriEl || !areaEl || !arcPath) return

  // Apply nozzle color to SVG elements
  arcPath.setAttribute('fill', nozzleColor + '33')
  arcPath.setAttribute('stroke', nozzleColor)
  const centerDot = document.querySelector('#calcViz circle:last-child')
  if (centerDot) centerDot.setAttribute('fill', nozzleColor)

  function update(): void {
    const units = getUnits()
    const arc = parseFloat(arcSlider!.value)
    const radiusM = parseFloat(radiusSlider!.value)
    const flowLpm = interpolate(arcsData, arc, radiusM)
    const areaM2 = (Math.PI * radiusM * radiusM * arc) / 360
    const precipSqMm = precipitationAt(arcsData, arc)
    const precipTriMm = precipSqMm / 0.866

    arcValue!.textContent = Math.round(arc).toString()
    radiusValue!.textContent = convertLength(radiusM, units).toFixed(1)
    flowEl!.textContent = convertFlow(flowLpm, units).toFixed(2)
    precipSqEl!.textContent = convertPrecipRate(precipSqMm, units).toFixed(units === 'imperial' ? 2 : 1)
    precipTriEl!.textContent = convertPrecipRate(precipTriMm, units).toFixed(units === 'imperial' ? 2 : 1)
    areaEl!.textContent = convertArea(areaM2, units).toFixed(1)

    arcPath!.setAttribute('d', drawArc(maxRadius, arc, radiusM))
  }

  arcSlider.addEventListener('input', update)
  radiusSlider.addEventListener('input', update)
  update()
}
